/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, useNavigate, useParams } from "react-router-dom";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React TS examples components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import MDInput from "components/MDInput";
import { useAppContext } from "context/app-context";
import { QuizDto } from "types";
import { Box, Button, Modal, Typography, useMediaQuery } from "@mui/material";
import MDBadge from "components/MDBadge";
import { getUUIDFromKey } from "utils/helpers";
import { useCognito } from "services/cognito-service";
import ReactPlayer from "react-player";
import { ElfsightWidget } from "react-elfsight-widget";

// Declaring prop types for DashboardNavbar
interface Props {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}
type LocationState = { title: string }

const style = {
  height: 'fit-content',
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: 730,
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

function DashboardNavbar({ absolute, light, isMini }: Props): JSX.Element {

  const [videoModal, setVideoModal] = useState(false)
  const [isTipsAndTricks, setIsTipsAndTricks] = useState<boolean>(null)

  const [navbarType, setNavbarType] = useState<
    "fixed" | "absolute" | "relative" | "static" | "sticky"
  >();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const location = useLocation()
  const route = location.pathname.split("/").slice(1);
  const [title, setTitle] = useState('')
  const params = useParams()
  let locationTitle: string

  if (location.state) {
    const state = location.state as LocationState
    locationTitle = state.title
  }

  const { id } = params
  useEffect(() => {
    if (id) {
      setTitle(locationTitle)
    } else {
      setTitle(route[route.length - 1])
    }
  }, [id])


  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }: {
    palette: any;
    functions: any;
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });
  const {isPlayCards} = useAppContext()
  const isMobile = useMediaQuery('(max-width:499px)')

  const handleVideoState = (value: string) => {
    if (value === 'tipsAndTricks') {
      setIsTipsAndTricks(true)
    } else if (value === 'howToPlay') {
      setIsTipsAndTricks(false)
    }
    setVideoModal(!videoModal)
  }


  return (
    !isPlayCards && <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Modal
        open={videoModal}
        onClose={() => setVideoModal(!videoModal)}>
        <MDBox sx={style}>
                <ReactPlayer width='auto' height={isMobile ? 'auto': 360} url={ isTipsAndTricks ? 'https://vimeo.com/854743161' : 'https://vimeo.com/854104906'} controls/>
        </MDBox>
      </Modal>
      <Toolbar sx={navbarContainer}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={title} route={route} light={light} />
        </MDBox>
        <MDBox
          marginLeft={isMobile ? 'auto': 0}
          display='flex'
          flexDirection={isMobile ?  'column': 'row'}
        >
          <Button disableRipple>
        <Typography fontSize={16} sx={iconsStyle} onClick={() => handleVideoState('howToPlay')}>Vejledning</Typography>
        </Button>
        <Button sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="large" disableRipple>
            <Icon fontSize="medium" sx={iconsStyle}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon> <Typography fontSize={16} sx={iconsStyle}>Vis menu</Typography>
        </Button>
        <Button disableRipple>
        <Typography fontSize={16} sx={iconsStyle} onClick={() => handleVideoState('tipsAndTricks')}>Tips & Trick</Typography></Button>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={navbarDesktopMenu}>
            {/* <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> 
               <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleOpenMenu}
              >
                <MDBadge badgeContent={9} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {renderMenu()}
            </MDBox> */}
          </MDBox>
        )}
      </Toolbar>
      <ElfsightWidget  widgetId='9acc225a-e794-4bdc-b7c4-03b40d7c1b7f' />
    </AppBar >
  );
}

// Declaring default props for DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

export default DashboardNavbar;
